import { AsyncPipe } from '@angular/common';
import { Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
	AbstractControl,
	FormControl,
	FormGroup,
	ReactiveFormsModule,
	ValidationErrors,
	ValidatorFn,
	Validators,
} from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { AlertComponent, ISelect, ModalComponent, SelectComponent } from '@tecalisdev/infinity';
import { LanguageService } from 'src/app/core/services/language.service';
import { MasterService } from '../../services/master.service';
import { LanguagePickerUiService } from '../../services/language-picker-ui.service';

@Component({
	selector: 'app-modal-change-language',
	imports: [AsyncPipe, ModalComponent, SelectComponent, TranslatePipe, ReactiveFormsModule, AlertComponent],
	templateUrl: './modal-change-language.component.html',
	styleUrl: './modal-change-language.component.scss',
})
export class ModalChangeLanguageComponent {
	private languageService = inject(LanguageService);
	private masterService = inject(MasterService);
	private trans = inject(TranslateService);
	private destroyRef = inject(DestroyRef);
	protected pickerUi = inject(LanguagePickerUiService);
	public languageForm = new FormGroup({
		locale: new FormControl(this.languageService.getLanguage(), {
			nonNullable: true,
			validators: [Validators.required, this.allowedLanguageValidator()],
		}),
	});

	constructor() {
		this.languageForm.controls.locale.valueChanges.subscribe((raw) => {
			const code = this.normalizeLocaleValue(raw);
			if (!code || !this.masterService.isAllowedLanguageCode(code)) {
				return;
			}
			if (code === this.languageService.getLanguage() && this.trans.getCurrentLang() === code) {
				return;
			}
			this.selectLanguage(code);
		});

		this.trans.onLangChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
			const current = this.languageService.getLanguage();
			this.languageForm.controls.locale.patchValue(current, { emitEvent: false });
		});
	}

	get hasLanguageOptions(): boolean {
		return this.masterService.getLanguages().length > 0;
	}

	get languageSelectOptions(): ISelect[] {
		return this.masterService.getLanguages().map((opt) => ({
			id: opt.code,
			label: this.trans.instant(opt.labelKey),
			translate: true,
		}));
	}

	private allowedLanguageValidator(): ValidatorFn {
		return (control: AbstractControl): ValidationErrors | null => {
			const code = this.normalizeLocaleValue(control.value);
			if (code == null || code === '') {
				return null;
			}
			return this.masterService.isAllowedLanguageCode(code) ? null : { allowedLanguage: true };
		};
	}

	/** tec-select puede propagar string o ISelect; normalizamos a código. */
	private normalizeLocaleValue(raw: unknown): string | null {
		if (raw == null || raw === '') {
			return null;
		}
		if (typeof raw === 'object' && raw !== null && 'id' in raw) {
			return String((raw as ISelect).id);
		}
		return String(raw);
	}

	closeModal(): void {
		this.pickerUi.close();
	}

	selectLanguage(code: string): void {
		this.languageService.setLanguage(code);
		this.languageForm.controls.locale.patchValue(code, { emitEvent: false });
		this.closeModal();
	}
}
